import {
  configureErrorReporter as configureCoreReporter,
  reportClientError,
  describeThrown,
} from '@adrodd/core';

/**
 * Browser-side wiring for the shared crash reporter.
 *
 * React error boundaries only catch errors thrown during render. A crash in an
 * event handler, a timer, or a rejected promise never reaches one — which is
 * most of what actually breaks for a reader — so those are caught here too.
 */
export function configureErrorReporter(): void {
  configureCoreReporter(() => ({
    os: navigator.platform || undefined,
    // The browser+version an admin needs to reproduce; the raw UA string goes
    // along separately, server-side, from the request header.
    device_model: browserLabel(),
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    locale: navigator.language,
  }));
}

/** "Chrome 141" / "Safari 18" — the UA string parsed down to what's useful. */
function browserLabel(): string | undefined {
  const ua = navigator.userAgent;
  const match =
    /(Edg|OPR)\/(\d+)/.exec(ua) ??
    /(Chrome)\/(\d+)/.exec(ua) ??
    /(Firefox)\/(\d+)/.exec(ua) ??
    /Version\/(\d+).*(Safari)/.exec(ua);
  if (!match) return undefined;
  const names: Record<string, string> = { Edg: 'Edge', OPR: 'Opera' };
  // The Safari pattern captures version first, name second.
  const [, a, b] = match;
  const isSafariShape = b === 'Safari';
  const name = isSafariShape ? 'Safari' : (names[a] ?? a);
  const version = isSafariShape ? a : b;
  return `${name} ${version}`;
}

// Errors thrown by a browser extension's injected script are not ours to fix.
const fromExtension = (thrown: unknown): boolean =>
  /(chrome|moz|safari(-web)?)-extension:\/\//.test(String((thrown as Error | undefined)?.stack ?? ''));

// Outlook SafeLinks / Bing link-preview crawlers (CefSharp) inject a script that
// rejects with this; it is their bridge failing, not our code.
const fromLinkCrawler = (thrown: unknown): boolean =>
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(describeThrown(thrown).message);

let installed = false;

export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    // Resource load failures (a broken <img>) also fire this, with no error
    // object. They are not crashes and would drown the real ones.
    if (!event.error || fromExtension(event.error)) return;
    reportClientError({
      ...describeThrown(event.error),
      where: window.location.pathname,
      fatal: false,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (fromExtension(event.reason) || fromLinkCrawler(event.reason)) return;
    reportClientError({
      ...describeThrown(event.reason),
      kind: `Unhandled${describeThrown(event.reason).kind}`,
      where: window.location.pathname,
      fatal: false,
    });
  });
}
